/**
 * Les box d'un site et la file qui les dessert — règles pures (RG-PA-10).
 *
 * Deux choses se décident ici, sans toucher à la base :
 * - **quels box un site doit avoir** : un par médecin prévu dans sa dotation,
 *   de la prestation de sa ligne. Les box suivent la dotation ; personne ne
 *   les crée à la main, et ils ne peuvent donc plus la contredire ;
 * - **dans quel box placer un patient** : l'accueil dit la prestation, la file
 *   choisit le box ouvert de cette prestation qui a le moins de monde.
 */
import { ROLES_MEDICAUX } from '../constants/roles.js';
import { PRESTATION_PAR_DEFAUT, prestationDeLigne, ROLE_EN_BOX, } from './sites.js';
/**
 * « GÉNÉRALISTE » → « Généraliste » : le nom que porte la porte du box. Un
 * sigle court reste en capitales : ORL, CPN.
 */
export function libelleDePrestation(prestation) {
    const p = prestation.trim();
    if (/^[A-ZÀ-Ý]{2,3}$/.test(p))
        return p;
    const bas = p.toLowerCase();
    return bas.charAt(0).toUpperCase() + bas.slice(1);
}
/** Deux prestations sont la même quelle que soit la casse : « dentiste » est DENTISTE. */
export function memePrestation(a, b) {
    return a.trim().toUpperCase() === b.trim().toUpperCase();
}
/**
 * La forme d'un numéro de porte qui sert à comparer : sans casse, sans
 * accent, espaces resserrés. « porte  1 » et « Porte 1 » sont la même porte.
 */
export function clePorte(porte) {
    return porte.normalize('NFD').replace(/\p{M}/gu, '').trim().replace(/\s+/g, ' ').toUpperCase();
}
/**
 * Le numéro de porte dit-il la prestation ? « Dentiste 1 » ou « CPN 2 »
 * annonceraient la spécialité à toute la salle d'attente : c'est une donnée
 * de santé. La prestation doit y figurer en mots entiers pour compter.
 */
export function porteCiteLaPrestation(porte, prestation) {
    const mots = (texte) => clePorte(texte)
        .split(/[^A-Z0-9]+/)
        .filter((m) => m !== '')
        .join(' ');
    const p = mots(prestation);
    return p !== '' && ` ${mots(porte)} `.includes(` ${p} `);
}
/** Le nombre de box que la dotation prévoit, par prestation. */
export function boxesPrevus(effectifs) {
    const prevus = new Map();
    for (const ligne of effectifs) {
        if (ligne.role !== ROLE_EN_BOX)
            continue;
        const p = prestationDeLigne(ligne);
        prevus.set(p, (prevus.get(p) ?? 0) + ligne.effectifDeclare);
    }
    return prevus;
}
function numero(nom) {
    const m = /(\d+)\s*$/.exec(nom);
    return m === null ? Number.MAX_SAFE_INTEGER : Number(m[1]);
}
/**
 * Ce qu'il faut faire pour que les box d'un site correspondent à sa dotation.
 *
 * - Il en manque : on remet d'abord en service ceux qu'on avait retirés pour
 *   cette prestation (leurs passages passés y restent attachés), puis on crée
 *   « Dentiste 1 », « Dentiste 2 »… au premier numéro libre.
 * - Il y en a trop : on retire les derniers, jamais celui qu'un praticien
 *   tient ; ceux-là sont rendus dans `tenus`, et l'appelant refuse.
 * - Une prestation qui n'est plus dans la dotation perd tous ses box.
 */
export function planDesBoxes(effectifs, existants, 
/** Des noms pris ailleurs (un box supprimé garde le sien) : jamais réutilisés. */
nomsReserves = []) {
    const prevus = boxesPrevus(effectifs);
    const prestations = new Set(prevus.keys());
    for (const b of existants)
        if (b.actif)
            prestations.add(b.prestation.toUpperCase());
    const nomsPris = new Set([...existants.map((b) => b.nom), ...nomsReserves].map((n) => n.toUpperCase()));
    const aRemettre = [];
    const aCreer = [];
    const aRetirer = [];
    const tenus = [];
    for (const prestation of [...prestations].sort()) {
        const voulus = prevus.get(prestation) ?? 0;
        const duMeme = existants.filter((b) => memePrestation(b.prestation, prestation));
        const actifs = duMeme.filter((b) => b.actif);
        if (actifs.length < voulus) {
            let manquants = voulus - actifs.length;
            const retires = duMeme
                .filter((b) => !b.actif)
                .sort((a, b) => numero(a.nom) - numero(b.nom) || a.nom.localeCompare(b.nom));
            for (const b of retires.slice(0, manquants))
                aRemettre.push(b.id);
            manquants -= Math.min(manquants, retires.length);
            const libelle = libelleDePrestation(prestation);
            for (let n = 1; manquants > 0; n++) {
                const nom = `${libelle} ${String(n)}`;
                if (nomsPris.has(nom.toUpperCase()))
                    continue;
                nomsPris.add(nom.toUpperCase());
                aCreer.push({ nom, prestation, ordre: n });
                manquants--;
            }
        }
        else if (actifs.length > voulus) {
            const enTrop = actifs.length - voulus;
            // Les derniers d'abord : « Généraliste 3 » part avant « Généraliste 1 ».
            const parFin = [...actifs].sort((a, b) => b.ordre - a.ordre || numero(b.nom) - numero(a.nom) || b.nom.localeCompare(a.nom));
            const libres = parFin.filter((b) => !b.tenu);
            for (const b of libres.slice(0, enTrop))
                aRetirer.push(b.id);
            if (libres.length < enTrop) {
                tenus.push(...parFin.filter((b) => b.tenu).slice(0, enTrop - libres.length));
            }
        }
    }
    return { aRemettre, aCreer, aRetirer, tenus };
}
// ─────────────────────────────────────────────────────────────────────
//  Les spécialités d'un compte médical
// ─────────────────────────────────────────────────────────────────────
/**
 * Les spécialités qu'un compte exerce, rangées : en majuscules, sans
 * doublon. Un rôle médical en a au moins une — généraliste s'il n'en dit
 * pas plus. Les autres rôles n'en ont aucune.
 *
 * La spécialité n'est PAS un rôle : elle ne donne aucun droit. Les droits
 * viennent du rôle (ADR 0006) ; la spécialité dit seulement quels box le
 * praticien peut tenir et dans quelle ligne de la dotation il compte.
 */
export function specialitesDuCompte(role, specialites) {
    if (!ROLES_MEDICAUX.includes(role))
        return [];
    const rangees = [
        ...new Set((specialites ?? []).map((s) => s.trim().toUpperCase()).filter((s) => s !== '')),
    ];
    return rangees.length === 0 ? [PRESTATION_PAR_DEFAUT] : rangees;
}
/** Un praticien ne tient que les box de ses spécialités : un généraliste n'ouvre pas le cabinet dentaire. */
export function peutTenirLeBox(specialites, prestationDuBox) {
    return specialites.some((s) => memePrestation(s, prestationDuBox));
}
//# sourceMappingURL=boxes.js.map